// Revolut-partner (counterparty) címének felbontása — országfüggő, tiszta függvény.
//
// ⚠⚠ 2026-09-24, élő hiba: a „Partner felvétele" a Revolut sandboxon 400-at kapott
//   („postcode is empty"). A Refinitiv Ltd (GB IBAN) címe `Five Canada Square, Canary Wharf,
//   London E14 5AQ, United Kingdom` volt; a régi `parseAddress` csak 3–5 számjegyes
//   irányítószámot ismert, a brit postcode-ot nem → az `address` postcode nélkül ment ki, a
//   város „Five" lett, a `street_line1` a teljes cím.
//
// GYÁRTÓI SZABÁLY (a Revolut Business API hivatalos OpenAPI-ja — CLAUDE.md §3.2):
//   * forrás: https://github.com/revolut-engineering/revolut-openapi/blob/master/yaml/business.yaml
//     (2026-09-24-én letöltve; azonos a `raw/revolut-business-openapi-2026-09-06.yaml`-lal),
//     `POST /counterparty` → `CreateCounterpartyRequest.address` = `BeneficiaryAddress`,
//     referencia: https://developer.revolut.com/docs/api/business#add-counterparty
//   * az `address` objektum a kérésben NEM kötelező; ha VAN, akkor a `country` (ISO 3166
//     2 betű, `^[A-Z]{2}$`) és a `postcode` KÖTELEZŐ. A többi (`street_line1`, `street_line2`,
//     `region`, `city`) opcionális, de a Revolut kifejezetten AJÁNLJA mindet („Some payment
//     rails may have stricter requirements … for example, by also requiring the `region`
//     field").
//   * ⚠ A létrehozó sémán (`BeneficiaryAddress`) NINCS hosszkorlát. A korlátokat a válasz
//     (`CounterpartyAddress`) és a módosító kérés (`UpdateCounterpartyPaymentMethodAddress`)
//     sémája adja: street_line1/2 ≤ 100, region ≤ 50, city ≤ 50, postcode ≤ 15. Ezeket
//     alkalmazzuk — [Következtetés]: amit a Revolut vissza sem tud adni, azt ne küldjük.
//   * [Nem egyértelmű a doksiban] hogy mely országban / devizában / utalási úton kötelező
//     maga az `address` (a guide „depending on the country" fogalmaz, táblázat nélkül).
//     Ezért NEM találunk ki országszabályt: ha bármilyen cím-adat megy, a postcode kötelező;
//     ha semmilyen, a cím kimarad (a gyártói séma ezt engedi).

export const REVOLUT_ADDRESS_LIMITS = Object.freeze({
  street_line1: 100, street_line2: 100, region: 50, city: 50, postcode: 15,
});

// Cím végi országnév → ISO kód (angol, helyi, magyar alak). Kisbetűs, ékezet nélküli kulcs.
const COUNTRY_NAMES = {
  'united kingdom': 'GB', 'uk': 'GB', 'u.k.': 'GB', 'great britain': 'GB', 'england': 'GB',
  'scotland': 'GB', 'wales': 'GB', 'northern ireland': 'GB', 'egyesult kiralysag': 'GB',
  'nagy-britannia': 'GB', 'anglia': 'GB',
  'ireland': 'IE', 'eire': 'IE', 'irorszag': 'IE',
  'netherlands': 'NL', 'the netherlands': 'NL', 'nederland': 'NL', 'hollandia': 'NL',
  'germany': 'DE', 'deutschland': 'DE', 'nemetorszag': 'DE',
  'austria': 'AT', 'osterreich': 'AT', 'ausztria': 'AT',
  'switzerland': 'CH', 'schweiz': 'CH', 'suisse': 'CH', 'svajc': 'CH',
  'france': 'FR', 'franciaorszag': 'FR',
  'italy': 'IT', 'italia': 'IT', 'olaszorszag': 'IT',
  'spain': 'ES', 'espana': 'ES', 'spanyolorszag': 'ES',
  'belgium': 'BE', 'belgique': 'BE', 'belgie': 'BE',
  'denmark': 'DK', 'danmark': 'DK', 'dania': 'DK',
  'czech republic': 'CZ', 'czechia': 'CZ', 'cesko': 'CZ', 'ceska republika': 'CZ',
  'csehorszag': 'CZ',
  'slovakia': 'SK', 'slovensko': 'SK', 'szlovakia': 'SK',
  'poland': 'PL', 'polska': 'PL', 'lengyelorszag': 'PL',
  'hungary': 'HU', 'magyarorszag': 'HU',
  'luxembourg': 'LU', 'sweden': 'SE', 'norway': 'NO', 'finland': 'FI', 'portugal': 'PT',
  'romania': 'RO', 'romania.': 'RO', 'croatia': 'HR', 'slovenia': 'SI', 'estonia': 'EE',
  'united states': 'US', 'usa': 'US',
};

// Irányítószám-minták. `city` = hol áll a város a postcode-hoz képest ('before' | 'after').
const D = (n) => `(?:[A-Z]{1,2}-)?(\\d{${n}})`;   // „D-12345", „CH-8001" előtag is
const POSTCODE_RULES = {
  GB: { re: /\b(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})\b/i, city: 'before',
        fmt: (m) => m[1].toUpperCase().replace(/\s+/g, '').replace(/^(.+)(\d[A-Z]{2})$/, '$1 $2') },
  IE: { re: /\b([AC-FHKNPRTV-Y]\d{2}|D6W) ?([0-9AC-FHKNPRTV-Y]{4})\b/i, city: 'before',
        fmt: (m) => `${m[1]} ${m[2]}`.toUpperCase() },
  NL: { re: /\b(\d{4}) ?([A-Z]{2})\b/, city: 'after', fmt: (m) => `${m[1]} ${m[2]}` },
  CZ: { re: /\b(\d{3}) ?(\d{2})\b/, city: 'after', fmt: (m) => `${m[1]} ${m[2]}` },
  SK: { re: /\b(\d{3}) ?(\d{2})\b/, city: 'after', fmt: (m) => `${m[1]} ${m[2]}` },
  PL: { re: /\b(\d{2}-\d{3})\b/, city: 'after', fmt: (m) => m[1] },
  DE: { re: new RegExp(`\\b${D(5)}\\b`), city: 'after', fmt: (m) => m[1] },
  FR: { re: new RegExp(`\\b${D(5)}\\b`), city: 'after', fmt: (m) => m[1] },
  IT: { re: new RegExp(`\\b${D(5)}\\b`), city: 'after', fmt: (m) => m[1] },
  ES: { re: new RegExp(`\\b${D(5)}\\b`), city: 'after', fmt: (m) => m[1] },
  AT: { re: new RegExp(`\\b${D(4)}\\b`), city: 'after', fmt: (m) => m[1] },
  CH: { re: new RegExp(`\\b${D(4)}\\b`), city: 'after', fmt: (m) => m[1] },
  BE: { re: new RegExp(`\\b${D(4)}\\b`), city: 'after', fmt: (m) => m[1] },
  DK: { re: new RegExp(`\\b${D(4)}\\b`), city: 'after', fmt: (m) => m[1] },
  HU: { re: new RegExp(`\\b${D(4)}\\b`), city: 'after', fmt: (m) => m[1] },
};
// Ismeretlen ország: 3–6 számjegy — bizonytalan, a felületen a tulajdonos javítja.
const GENERIC_RULE = { re: /\b(\d{3,6})\b/, city: 'after', fmt: (m) => m[1] };

const fold = (s) => String(s || '').normalize('NFD').replace(/\p{M}/gu, '')
  .toLowerCase().trim();
const clean = (s) => String(s || '').replace(/\s+/g, ' ').replace(/^[\s,;.-]+|[\s,;-]+$/g, '').trim();

/** A cím végéről levágja az országnevet (vagy az ISO kódot). → [maradék, iso|null] */
function stripCountry(segments) {
  if (!segments.length) return [segments, null];
  const last = segments[segments.length - 1];
  const key = fold(last).replace(/\.$/, '');
  if (COUNTRY_NAMES[key]) return [segments.slice(0, -1), COUNTRY_NAMES[key]];
  if (/^[A-Z]{2}$/.test(last.trim()) && POSTCODE_RULES[last.trim()]) {
    return [segments.slice(0, -1), last.trim()];
  }
  // „London E14 5AQ United Kingdom" — vessző nélküli országnév a szegmens végén
  for (const [name, iso] of Object.entries(COUNTRY_NAMES)) {
    if (name.length < 4) continue;
    if (key.endsWith(` ${name}`)) {
      const cut = last.slice(0, last.length - name.length).trim();
      return [[...segments.slice(0, -1), cut], iso];
    }
  }
  return [segments, null];
}

/** Az első/utolsó olyan szegmens, amelyben a postcode a szegmens elején vagy végén áll;
 *  ha ilyen nincs, bármely találat. → { idx, match } | null */
function findPostcode(segments, rule) {
  const anchored = [];
  const any = [];
  segments.forEach((seg, idx) => {
    const g = new RegExp(rule.re.source, rule.re.flags.includes('g') ? rule.re.flags : rule.re.flags + 'g');
    let m;
    while ((m = g.exec(seg)) !== null) {
      const before = seg.slice(0, m.index).trim();
      const after = seg.slice(m.index + m[0].length).trim();
      const hit = { idx, match: m, before, after };
      any.push(hit);
      if (!before || !after) anchored.push(hit);
      if (m[0].length === 0) g.lastIndex += 1;
    }
  });
  // Brit/ír: a postcode a cím VÉGE felé áll → az utolsó; kontinentális: az első horgonyzott.
  if (rule.city === 'before') return anchored[anchored.length - 1] || any[any.length - 1] || null;
  return anchored[0] || any[0] || null;
}

/** Önálló postcode-szegmens mellől a város-szegmens indexe (-1, ha nincs): a preferált
 *  sorrendben (utána távolság szerint) az első SZÁMJEGY NÉLKÜLI szegmens (a „Fő u. 1." utca, a „Budapest" város),
 *  ennek hiányában az első létező. */
function neighbour(segments, idx, order) {
  const rest = segments.map((_, i) => i).filter(i => !order.includes(i))
    .sort((a, b) => Math.abs(a - idx) - Math.abs(b - idx));
  const ok = [...order, ...rest].filter(i => i >= 0 && i < segments.length && i !== idx);
  return ok.find(i => !/\d/.test(segments[i])) ?? (ok.length ? ok[0] : -1);
}

/** Egyetlen utca-sztring hosszkorlátra vágása: ami az 1. sorba nem fér, szóhatáron a 2.
 *  sorba kerül (a 2. sor is ≤ 100). */
function splitStreet(str) {
  const L = REVOLUT_ADDRESS_LIMITS.street_line1;
  const s = clean(str);
  if (s.length <= L) return [s, ''];
  const cut = s.lastIndexOf(' ', L);
  const at = cut > L / 2 ? cut : L;
  return [s.slice(0, at).trim(), s.slice(at).trim().slice(0, REVOLUT_ADDRESS_LIMITS.street_line2)];
}

/**
 * Szabad szöveges cím → Revolut `address`-alak.
 *
 * @param {string|null} raw          a bizonylat `supplier_address`-e
 * @param {object}      opts
 * @param {string}      opts.ibanCountry  az IBAN első két betűje (tartalék ország)
 * @param {object|null} opts.navAddress   a NAV-ból jött strukturált szállítói cím
 *        (`{country, postcode, city, street, region}` — a backend `supplier_address_nav`-ja);
 *        ha van, ELSŐKÉNT ezt használjuk: gyártói adat, nem szövegből kitalált.
 * @returns {{street_line1:string, street_line2:string, postcode:string, city:string,
 *            region:string, country:string, source:'nav'|'parsed'|'empty'}}
 */
export function parseCounterpartyAddress(raw, { ibanCountry = '', navAddress = null } = {}) {
  const L = REVOLUT_ADDRESS_LIMITS;
  const fallbackCountry = /^[A-Z]{2}$/.test(ibanCountry || '') ? ibanCountry : 'HU';

  if (navAddress && (navAddress.postcode || navAddress.city || navAddress.street)) {
    const [s1, s2] = splitStreet(navAddress.street);
    return {
      street_line1: s1, street_line2: s2 || '',
      postcode: clean(navAddress.postcode).slice(0, L.postcode),
      city: clean(navAddress.city).slice(0, L.city),
      region: clean(navAddress.region).slice(0, L.region),
      country: /^[A-Z]{2}$/.test(navAddress.country || '') ? navAddress.country : fallbackCountry,
      source: 'nav',
    };
  }

  const text = String(raw || '').replace(/\r/g, '').trim();
  if (!text) {
    return { street_line1: '', street_line2: '', postcode: '', city: '', region: '',
             country: fallbackCountry, source: 'empty' };
  }
  let segments = text.split(/[\n,;]+/).map(clean).filter(Boolean);
  const [rest, named] = stripCountry(segments);
  segments = rest.map(clean).filter(Boolean);
  const country = named || fallbackCountry;
  const rule = POSTCODE_RULES[country] || GENERIC_RULE;

  let postcode = '';
  let city = '';
  let cityIdx = -1;          // melyik szegmens „fogyott el" városként
  const streetParts = [...segments];
  const hit = findPostcode(segments, rule);
  if (hit) {
    postcode = rule.fmt(hit.match);
    const others = segments.length > 1;
    const { idx, before, after } = hit;
    let streetRemainder = '';
    if (rule.city === 'before') {
      // „London E14 5AQ" → város = a postcode előtti rész. Ha a szegmens egyben az utca is
      // („Five Canada Square London E14 5AQ", vessző nélkül), a város az utolsó szó.
      if (before && others) { city = before; }
      else if (before) {
        const w = before.split(' ');
        city = w.pop(); streetRemainder = w.join(' ');
      } else if (after) { city = after; }
      // Brit/ír szokás: a postcode előtti szegmens a város/posta-település — akkor is, ha
      // számot tartalmaz („Dublin 18, D18 P521").
      else { cityIdx = idx > 0 ? idx - 1 : neighbour(segments, idx, [idx + 1]); }
    } else {
      // „1234 Budapest" / „10115 Berlin" / „1234 AB Amsterdam" → város = a postcode utáni rész.
      // Vessző nélkül („1051 Budapest Fő utca 1.") a város az első szó, a többi utca.
      if (after && others) { city = after; if (before) streetRemainder = before; }
      else if (after) {
        const w = after.split(' ');
        city = w.shift(); streetRemainder = [before, w.join(' ')].filter(Boolean).join(' ');
      } else {
        cityIdx = neighbour(segments, idx, [idx + 1, idx - 1]);
        if (before) streetRemainder = before;
      }
    }
    if (cityIdx >= 0) city = segments[cityIdx];
    streetParts.splice(0, streetParts.length,
      ...segments.map((s, i) => (i === idx ? clean(streetRemainder) : i === cityIdx ? '' : s)));
  }
  const parts = streetParts.map(clean).filter(Boolean);
  const [s1, s2] = parts.length > 1
    ? [parts[0].slice(0, L.street_line1), parts.slice(1).join(', ').slice(0, L.street_line2)]
    : splitStreet(parts[0] || '');
  return {
    street_line1: clean(s1), street_line2: clean(s2 || ''),
    postcode: clean(postcode).slice(0, L.postcode),
    city: clean(city).slice(0, L.city),
    region: '',
    country, source: 'parsed',
  };
}

/** Mezőnkénti hibák a Revolut gyártói sémája szerint (lásd a fájl fejét). Üres objektum =
 *  küldhető. A magyar üzenet a mező alá kerül a felületen. */
export function counterpartyFieldErrors(d) {
  const err = {};
  const letters = (String(d?.name || '').match(/\p{L}/gu) || []).length;
  if (letters < 2) err.name = 'A partner neve legalább 2 betű legyen (Revolut-szabály).';
  if (!/^[A-Z]{2}$/.test(String(d?.country || '').trim().toUpperCase())) {
    err.country = 'Az ország 2 betűs kódja kötelező a Revolut-partnerhez (pl. GB, HU).';
  }
  const anyAddress = ['street_line1', 'street_line2', 'city', 'postcode']
    .some(k => String(d?.[k] || '').trim());
  if (anyAddress && !String(d?.postcode || '').trim()) {
    err.postcode = 'Irányítószám kötelező a Revolut-partnerhez.';
  }
  for (const [k, max] of Object.entries(REVOLUT_ADDRESS_LIMITS)) {
    if (String(d?.[k] || '').length > max) err[k] = `Legfeljebb ${max} karakter (Revolut-korlát).`;
  }
  return err;
}

/** A szerkesztett mezőkből a `POST /revolut/counterparties` `address`-e — vagy null, ha
 *  semmilyen cím-adat nincs (a gyártói séma szerint ilyenkor a cím elhagyható). */
export function counterpartyAddressPayload(d) {
  const t = (k) => String(d?.[k] || '').trim();
  if (!['street_line1', 'street_line2', 'city', 'postcode'].some(k => t(k))) return null;
  const out = { country: t('country').toUpperCase(), postcode: t('postcode') };
  for (const k of ['street_line1', 'street_line2', 'city', 'region']) if (t(k)) out[k] = t(k);
  return out;
}

/** A Revolut 400-as üzenete → érthető magyar mondat a mező nevével (a nyers angol mellé). */
const FIELD_HU = {
  postcode: 'az irányítószám', city: 'a város', street_line1: 'az utca, házszám',
  street_line2: 'a cím 2. sora', region: 'a megye/régió', country: 'az ország',
  company_name: 'a partner neve', name: 'a partner neve', iban: 'az IBAN', bic: 'a BIC/SWIFT kód',
  currency: 'a deviza', bank_country: 'a bank országa', address: 'a cím',
};
export function describeRevolutCounterpartyError(raw) {
  const msg = String(raw || '').trim();
  if (!msg) return null;
  const low = msg.toLowerCase();
  const field = Object.keys(FIELD_HU).sort((a, b) => b.length - a.length)
    .find(k => low.includes(k.replace('_', ' ')) || low.includes(k));
  if (!field) return `A Revolut elutasította a partnert. (Revolut: „${msg}")`;
  const what = FIELD_HU[field];
  const kind = /empty|required|missing|must not be (blank|null)|is blank/.test(low) ? `hiányzik ${what}`
    : /too long|length|exceed/.test(low) ? `túl hosszú ${what}`
    : /invalid|not valid|format|must match/.test(low) ? `érvénytelen ${what}`
    : `gond van ezzel: ${what}`;
  return `A Revolut elutasította a partnert: ${kind}. (Revolut: „${msg}")`;
}
